import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { OffersGridLocation, useGetSurveysTotalCountQuery } from '@ocx/graphql';
import { QRCodeIcon, TabBarHomeIcon, TabBarPayAtPumpIcon } from '@ocx/ui-icons';
import { useOrderAheadNavigationOnClick, getOrderAheadTabIcon } from '@ocx/feat-order-ahead';
import { useMembership } from '@ocx/data-membership';
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { AppRoutePath } from '@ocx-app/pages/AppRoutePath';
import { getSurveysTabBarIcon } from '@ocx-app/modules/survey/survey.utils';

import { getOffersTabIcon } from './offers-tab-icon.utils';
import { BottomNavigationTabProps } from './bottom-navigation-tab.component';
import { messages } from './bottom-navigation.messages';
import { useStartPayInside } from './features/start-pay-inside/use-start-pay-inside.hook';
import { useStartPayAtPump } from './features/start-pay-at-pump/use-start-pay-at-pump.hook';

export const useBottomNavigationController = () => {
  const intl = useIntl();
  const {
    membership: { hasEmail },
  } = useMembership();
  const {
    config: { payAtPump, payInside, wallet, orderAhead, userExperience, surveys },
  } = useConfiguration();
  const { data: surveysTotalCountData } = useGetSurveysTotalCountQuery({
    skip: !surveys.enabled,
  });

  const { isEmailCompleted, isPayAtPumpEnabled, isPayInsideEnabled, isOffersPageEnabled } = useMemo(
    () => ({
      isEmailCompleted: hasEmail,
      isPayAtPumpEnabled: payAtPump.enabled,
      isPayInsideEnabled: payInside.enabled && wallet.enabled,
      isOffersPageEnabled: userExperience.offers.gridLocation === OffersGridLocation.Tab,
    }),
    [hasEmail, payAtPump.enabled, payInside.enabled, wallet.enabled, userExperience.offers.gridLocation],
  );

  const startPayAtPump = useStartPayAtPump({ isEmailCompleted });
  const startPayInside = useStartPayInside({ isEmailCompleted });
  const { pushToSurveys, pushToHome, pushToOffers } = useGetNavigationActions();
  const handleOrderAheadNavigationClick = useOrderAheadNavigationOnClick();

  return useMemo(() => {
    const tabs: BottomNavigationTabProps[] = [
      {
        value: 'tabBar:homeButtonText',
        label: intl.formatMessage(messages['tabBar:homeButtonText']),
        Icon: TabBarHomeIcon,
        routerPath: AppRoutePath.Home,
        onClick: pushToHome,
      },
    ];

    if (isOffersPageEnabled) {
      tabs.push({
        value: 'tabBar:offersButtonText',
        label: intl.formatMessage(messages['tabBar:offersButtonText']),
        Icon: getOffersTabIcon(userExperience.offers.tabNavbarIcon),
        onClick: pushToOffers,
        routerPath: AppRoutePath.Offers,
      });
    }

    if (surveys.enabled) {
      tabs.push({
        value: 'tabBar:surveysButtonText',
        label: intl.formatMessage(messages['tabBar:surveysButtonText']),
        Icon: getSurveysTabBarIcon(surveysTotalCountData?.surveys.totalCount || 0),
        onClick: pushToSurveys,
        routerPath: AppRoutePath.Surveys,
      });
    }

    if (isPayAtPumpEnabled) {
      tabs.push({
        value: 'tabBar:payAtPumpButtonText',
        label: intl.formatMessage(messages['tabBar:payAtPumpButtonText']),
        Icon: TabBarPayAtPumpIcon,
        onClick: startPayAtPump,
        routerPath: AppRoutePath.PayAtPump,
      });
    }
    if (isPayInsideEnabled) {
      tabs.push({
        value: 'tabBar:payInsideButtonText',
        label: intl.formatMessage(messages['tabBar:payInsideButtonText']),
        Icon: QRCodeIcon,
        onClick: startPayInside,
        routerPath: AppRoutePath.PayInside,
      });
    }
    if (orderAhead.enabled) {
      tabs.push({
        value: 'tabBar:orderAheadButtonText',
        label: orderAhead.navbar.text,
        Icon: getOrderAheadTabIcon(orderAhead.navbar.icon),
        onClick: handleOrderAheadNavigationClick,
        routerPath: AppRoutePath.OrderAhead,
      });
    }

    return { tabs };
  }, [
    intl,
    isOffersPageEnabled,
    isPayAtPumpEnabled,
    isPayInsideEnabled,
    orderAhead,
    surveys.enabled,
    userExperience.offers.tabNavbarIcon,
    surveysTotalCountData?.surveys.totalCount,
    pushToHome,
    pushToOffers,
    pushToSurveys,
    startPayAtPump,
    startPayInside,
    handleOrderAheadNavigationClick,
  ]);
};
